import moment from 'moment';
import posthogJs from 'posthog-js';

import {
  AUTO_LESSON_ANALYSIS_FEATURE_FLAG,
  TAutoAnalysisSurface,
} from '../constants/autoAnalysis';
import { SUBSTITUTE_TUTOR_BOOKING_FEATURE_FLAG } from '../constants/substituteTutorBooking';
import { BOOK_CLASS_FOCUS_FEATURE_FLAG } from '../constants/bookClassFocus';
import { RECURRING_BOOKING_FEATURE_FLAG } from '../constants/recurringBooking';
import {
  COURSE_PLAN_CHANGE_FEATURE_FLAG,
  COURSE_PLAN_UPGRADE_FEATURE_FLAG,
} from '../constants/coursePlanChange';

const timeFormat = 'YYYY-MM-DD HH:mm:ss';
const timestamp = moment().format(timeFormat);

/**
 * Feature flags that should be re-evaluated for the identified user rather than
 * carried over from the anonymous session when calling `posthog.identify`.
 *
 * `posthog.featureFlags.override()` (see App.tsx) is a persisted client-side
 * hard override: once a flag is carried over via the anonymous bucket it
 * ignores the flag's rollout/variant config in PostHog forever on that browser.
 * Any flag
 * that is an active experiment or rollout MUST be in this list, otherwise a
 * user who logged in once while it served `test` stays on `test` even after the
 * flag is set to 100% control.
 */
export const ANON_FLAGS_DELETE_LIST: string[] = [
  BOOK_CLASS_FOCUS_FEATURE_FLAG,
  RECURRING_BOOKING_FEATURE_FLAG,
];

export const postHogTestBuilderShown = (posthog, data) => {
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('test_builder');
    posthog.capture('test_builder_shown', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogTestBuilderClicked = (posthog, data) => {
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('test_builder');
    posthog.capture('test_builder_clicked', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogTutorProfileBookingClicked = (posthog, data) => {
  posthog.capture('tutor_profile_booking_clicked', {
    ...data,
    timestamp,
  });
};

export const postHogTutorStrengthModalShown = (posthog, data) => {
  posthog.capture('tutor_strength_modal_shown', {
    ...data,
    timestamp,
  });
};

export const postHogTutorStrengthModalSubmitted = (posthog, data) => {
  posthog.capture('tutor_strength_modal_submitted', {
    ...data,
    timestamp,
  });
};

export const postHogBookTrialClassCtaShown = (posthog, data) => {
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('book_trial_class_cta');
    posthog.capture('book_trial_class_cta_shown', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogBookTrialClassCtaClicked = (posthog, data) => {
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('book_trial_class_cta');
    posthog.capture('book_trial_class_cta_clicked', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogTopicsCoveredButtonShown = (posthog, data) => {
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('trial-resource-button-validation');
    posthog.capture('topics_covered_button_shown', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogTopicsCoveredButtonClicked = (posthog, data) => {
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('trial-resource-button-validation');
    posthog.capture('topics_covered_button_clicked', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogTopicsCoveredModalOkayClicked = (posthog, data) => {
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('trial-resource-button-validation');
    posthog.capture('topics_covered_modal_okay_clicked', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogTopicsCoveredModalClosed = (posthog, data) => {
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('trial-resource-button-validation');
    posthog.capture('topics_covered_modal_closed', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogUniversityApplicationBannerShown = (posthog, data) => {
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('university_application_banner');
    posthog.capture('university_application_banner_shown', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogUniversityApplicationBannerClicked = (posthog, data) => {
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('university_application_banner');
    posthog.capture('university_application_banner_clicked', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogUniversityApplicationBannerDismissed = (posthog, data) => {
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('university_application_banner');
    posthog.capture('university_application_banner_dismissed', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogUploadButtonShown = (posthog, data) => {
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('Upload-button');
    posthog.capture('upload_button_shown', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogUploadButtonClicked = (posthog, data) => {
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('Upload-button');
    posthog.capture('upload_button_clicked', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogAiInstantSupportClicked = (posthog, data) => {
  posthog.capture('ai_instant_support_clicked', {
    ...data,
    timestamp,
  });
};

export const postHogWhatsAppSupportClicked = (posthog, data) => {
  posthog.capture('whatsapp_support_clicked', {
    ...data,
    timestamp,
  });
};

export const postHogAiInstantSupportMessageSent = (posthog, data) => {
  posthog.capture('ai_instant_support_message_sent', {
    ...data,
    timestamp,
  });
};

/** PostHog payloads use snake_case property names */
export const postHogTrialCancelModalViewed = (
  posthog,
  data: { classId: string; studentId: string }
) => {
  posthog.capture('trial_cancel_modal_viewed', {
    class_id: data.classId,
    student_id: data.studentId,
    timestamp,
  });
};

export const postHogTrialRescheduleModalViewed = (
  posthog,
  data: { classId: string; studentId: string }
) => {
  posthog.capture('trial_reschedule_modal_viewed', {
    class_id: data.classId,
    student_id: data.studentId,
    timestamp,
  });
};

export const postHogTrialCancelReasonSelected = (
  posthog,
  data: {
    mainReason: string;
    subReason: string;
    otherText?: string;
    classId: string;
    studentId: string;
  }
) => {
  posthog.capture('trial_cancel_reason_selected', {
    main_reason: data.mainReason,
    sub_reason: data.subReason,
    ...(data.otherText !== undefined ? { other_text: data.otherText } : {}),
    class_id: data.classId,
    student_id: data.studentId,
    timestamp,
  });
};
export const postHogTrialRescheduleReasonSelected = (
  posthog,
  data: {
    mainReason: string;
    subReason: string;
    otherText?: string;
    classId: string;
    studentId: string;
  }
) => {
  posthog.capture('trial_reschedule_reason_selected', {
    main_reason: data.mainReason,
    sub_reason: data.subReason,
    ...(data.otherText !== undefined ? { other_text: data.otherText } : {}),
    class_id: data.classId,
    student_id: data.studentId,
    timestamp,
  });
};

export const postHogTrialCancelQuickReasonSelected = (
  posthog,
  data: {
    quickReasonId: string;
    quickReasonLabel: string;
    classId: string;
    studentId: string;
  }
) => {
  posthog.capture('trial_cancel_quick_reason_selected', {
    quick_reason_id: data.quickReasonId,
    quick_reason_label: data.quickReasonLabel,
    class_id: data.classId,
    student_id: data.studentId,
    timestamp,
  });
};

export const postHogTrialCancelRescheduleOffered = (
  posthog,
  data: {
    quickReasonId: string;
    quickReasonLabel: string;
    classId: string;
    studentId: string;
  }
) => {
  posthog.capture('trial_cancel_reschedule_offered', {
    quick_reason_id: data.quickReasonId,
    quick_reason_label: data.quickReasonLabel,
    class_id: data.classId,
    student_id: data.studentId,
    timestamp,
  });
};

export const postHogTrialCancelRescheduleAccepted = (
  posthog,
  data: {
    quickReasonId: string;
    quickReasonLabel: string;
    classId: string;
    studentId: string;
  }
) => {
  posthog.capture('trial_cancel_reschedule_accepted', {
    quick_reason_id: data.quickReasonId,
    quick_reason_label: data.quickReasonLabel,
    class_id: data.classId,
    student_id: data.studentId,
    timestamp,
  });
};

export const postHogTrialCancelRescheduleDeclined = (
  posthog,
  data: {
    quickReasonId: string;
    quickReasonLabel: string;
    classId: string;
    studentId: string;
  }
) => {
  posthog.capture('trial_cancel_reschedule_declined', {
    quick_reason_id: data.quickReasonId,
    quick_reason_label: data.quickReasonLabel,
    class_id: data.classId,
    student_id: data.studentId,
    timestamp,
  });
};

export const postHogTrialCancelSubmitted = (
  posthog,
  data: { classId: string; studentId: string }
) => {
  posthog.capture('trial_cancel_submitted', {
    class_id: data.classId,
    student_id: data.studentId,
    timestamp,
  });
};

export const postHogTrialRescheduleSubmitted = (
  posthog,
  data: { classId: string; studentId: string }
) => {
  posthog.capture('trial_reschedule_submitted', {
    class_id: data.classId,
    student_id: data.studentId,
    timestamp,
  });
};

/**
 * Intent event for the "book a class" focus experiment: the student pressed a
 * "Book a Class" / "Book a Lesson" CTA that opens the booking modal. `source`
 * tells the two entry points apart (`hero` = no-upcoming-class prompt in the
 * course hero, `bottom_bar` = sticky bottom nav bar).
 * Fires for every variant — read `variant` to compare control vs test.
 */
export const postHogBookClassClicked = (
  posthog,
  data: {
    userId?: string;
    name?: string;
    role?: string;
    courseId?: string;
    curriculum?: string;
    subject?: string;
    grade?: string;
    source?: 'hero' | 'bottom_bar';
  }
) => {
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag(BOOK_CLASS_FOCUS_FEATURE_FLAG);
    posthog.capture('book_class_clicked', {
      user_id: data.userId,
      name: data.name,
      role: data.role,
      course_id: data.courseId,
      ...(data.curriculum ? { curriculum: data.curriculum } : {}),
      ...(data.subject ? { subject: data.subject } : {}),
      ...(data.grade ? { grade: data.grade } : {}),
      ...(data.source ? { source: data.source } : {}),
      variant,
      timestamp,
    });
  });
};

/**
 * Goal / final metric for the "book a class" focus experiment: a class was
 * actually booked (the booking API call succeeded). Fires for every variant —
 * break `class_booked` down by `variant` (or by `$feature/Class-booking-exp-new`
 * in a PostHog Experiment) to see which arm produced more bookings.
 */
export const postHogClassBooked = (
  posthog,
  data: {
    userId?: string;
    name?: string;
    role?: string;
    courseId?: string;
    tutorId?: string;
    curriculum?: string;
    subject?: string;
    grade?: string;
    durationMinutes?: number;
    isSubstituteTutor?: boolean;
  }
) => {
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag(BOOK_CLASS_FOCUS_FEATURE_FLAG);
    const recurringBookingVariant = posthog.getFeatureFlag(
      RECURRING_BOOKING_FEATURE_FLAG
    );
    posthog.capture('class_booked', {
      user_id: data.userId,
      name: data.name,
      role: data.role,
      course_id: data.courseId,
      ...(data.tutorId ? { tutor_id: data.tutorId } : {}),
      ...(data.curriculum ? { curriculum: data.curriculum } : {}),
      ...(data.subject ? { subject: data.subject } : {}),
      ...(data.grade ? { grade: data.grade } : {}),
      ...(data.durationMinutes
        ? { duration_minutes: data.durationMinutes }
        : {}),
      ...(typeof data.isSubstituteTutor === 'boolean'
        ? { is_substitute_tutor: data.isSubstituteTutor }
        : {}),
      variant,
      recurring_booking_variant: recurringBookingVariant,
      timestamp,
    });
  });
};

export const postHogMissedTrialBookAgainClicked = (
  posthog,
  data: {
    userId: string;
    classId: string;
    curriculum?: string;
    subject?: string;
    grade?: string;
  }
) => {
  posthog.capture('missed_trial_book_again_clicked', {
    user_id: data.userId,
    class_id: data.classId,
    ...(data.curriculum ? { curriculum: data.curriculum } : {}),
    ...(data.subject ? { subject: data.subject } : {}),
    ...(data.grade ? { grade: data.grade } : {}),
    timestamp,
  });
};

export const postHogCancelledTrialBookAgainClicked = (
  posthog,
  data: {
    userId: string;
    classId: string;
    curriculum?: string;
    subject?: string;
    grade?: string;
  }
) => {
  posthog.capture('cancelled_trial_book_again_clicked', {
    user_id: data.userId,
    class_id: data.classId,
    ...(data.curriculum ? { curriculum: data.curriculum } : {}),
    ...(data.subject ? { subject: data.subject } : {}),
    ...(data.grade ? { grade: data.grade } : {}),
    timestamp,
  });
};

export const postHogRateYourTutorCtaClicked = (
  posthog,
  data: { userId: string; courseId: string }
) => {
  if (!posthog) return;
  posthog.capture('rate_your_tutor_cta_clicked', {
    user_id: data.userId,
    course_id: data.courseId,
    timestamp,
  });
};

export const postHogRateYourTutorModalOpened = (
  posthog,
  data: { userId: string; courseId: string; trigger: 'cta' | 'auto' }
) => {
  if (!posthog) return;
  posthog.capture('rate_your_tutor_modal_opened', {
    user_id: data.userId,
    course_id: data.courseId,
    trigger: data.trigger,
    timestamp,
  });
};

export const postHogRateYourTutorModalSubmitted = (
  posthog,
  data: { userId: string; courseId: string; rating: number }
) => {
  if (!posthog) return;
  posthog.capture('rate_your_tutor_modal_submitted', {
    user_id: data.userId,
    course_id: data.courseId,
    rating: data.rating,
    timestamp,
  });
};

export const postHogRateYourTutorModalClosed = (
  posthog,
  data: { userId: string; courseId: string }
) => {
  if (!posthog) return;
  posthog.capture('rate_your_tutor_modal_closed', {
    user_id: data.userId,
    course_id: data.courseId,
    timestamp,
  });
};

/** PostHog payloads use snake_case property names */
export const postHogChatQuickActionsShown = (
  posthog,
  data: { userId: string; conversationId: string; courseId?: string }
) => {
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('quick-actions-chat-tutor-student');
    posthog.capture('chat_quick_actions_shown', {
      user_id: data.userId,
      conversation_id: data.conversationId,
      ...(data.courseId ? { course_id: data.courseId } : {}),
      variant,
      timestamp,
    });
  });
};

export const postHogChatQuickActionsOpened = (
  posthog,
  data: { userId: string; conversationId: string; courseId?: string }
) => {
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('quick-actions-chat-tutor-student');
    posthog.capture('chat_quick_actions_opened', {
      user_id: data.userId,
      conversation_id: data.conversationId,
      ...(data.courseId ? { course_id: data.courseId } : {}),
      variant,
      timestamp,
    });
  });
};

export const postHogChatQuickActionsOptionSelected = (
  posthog,
  data: {
    userId: string;
    conversationId: string;
    option: string;
    courseId?: string;
  }
) => {
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('quick-actions-chat-tutor-student');
    posthog.capture('chat_quick_actions_option_selected', {
      user_id: data.userId,
      conversation_id: data.conversationId,
      option: data.option,
      ...(data.courseId ? { course_id: data.courseId } : {}),
      variant,
      timestamp,
    });
  });
};

/** User opened the Profile page (any tab). */
export const postHogProfilePageViewed = (
  posthog,
  data?: Record<string, unknown>
) => {
  if (!posthog) return;
  posthog.capture('profile_page_viewed', {
    ...(data || {}),
    timestamp,
  });
};

/**
 * Student has the Refund Request tab visible in the profile sidebar
 * (PostHog flag active). Distinct from opening the tab content.
 */
export const postHogProfileRefundTabAvailable = (
  posthog,
  data: Record<string, unknown>
) => {
  if (!posthog) return;
  posthog.capture('profile_refund_tab_available', {
    ...data,
    timestamp,
  });
};

/** User opened the Refund Request panel (content mounted). */
export const postHogProfileRefundSectionViewed = (
  posthog,
  data?: Record<string, unknown>
) => {
  if (!posthog) return;
  posthog.capture('profile_refund_section_viewed', {
    ...(data || {}),
    timestamp,
  });
};

export type TIgcseGroupClassBannerAnalyticsPayload = {
  userId?: string;
  role?: string;
  featureFlag: string;
  featureFlagVariant: string | boolean | undefined;
  countryCode?: string;
  timezone?: string;
};

type TPosthogClient = {
  capture: (
    event: string,
    props?: Record<string, unknown>,
    options?: Record<string, unknown>
  ) => void;
  flush?: () => void | Promise<void>;
};

type TPosthogWithLoaded = TPosthogClient & { __loaded?: boolean };

/** Prefer React hook client; fall back to posthog-js singleton (same instance as PostHogProvider). */
const resolveIgcsePosthogClient = (
  hookClient: TPosthogClient | undefined
): TPosthogClient | undefined => {
  if (hookClient && typeof hookClient.capture === 'function') {
    return hookClient;
  }
  const singleton = (posthogJs as unknown) as TPosthogWithLoaded;
  if (typeof singleton.capture === 'function') {
    return singleton;
  }
  return undefined;
};

const flushPosthogClient = (client: TPosthogClient) => {
  if (typeof client.flush !== 'function') {
    return;
  }
  const result = client.flush();
  if (result != null && typeof (result as Promise<void>).then === 'function') {
    (result as Promise<void>).catch(() => undefined);
  }
};

/** PostHog `capture` third-argument options (SDK uses snake_case keys). */
const POSTHOG_CAPTURE_IMMEDIATE = JSON.parse(
  '{"send_instantly":true}'
) as Record<string, unknown>;

/** Student saw the IGCSE group-class promo banner (dashboard). */
export const postHogIgcseGroupClassBannerShown = (
  posthogFromHook: TPosthogClient | undefined,
  data: TIgcseGroupClassBannerAnalyticsPayload
): boolean => {
  const ph = resolveIgcsePosthogClient(posthogFromHook);
  if (!ph) return false;
  const loaded = (ph as TPosthogWithLoaded).__loaded;
  if (!loaded) return false;
  const result = ph.capture(
    'igcse_group_class_banner_shown',
    {
      userId: data.userId,
      role: data.role,
      featureFlag: data.featureFlag,
      featureFlagVariant: data.featureFlagVariant,
      countryCode: data.countryCode,
      timezone: data.timezone,
      timestamp,
    },
    POSTHOG_CAPTURE_IMMEDIATE
  );
  if (result === undefined) return false;
  flushPosthogClient(ph);
  return true;
};

/** Student clicked Register here on the IGCSE group-class banner. */
export const postHogIgcseGroupClassBannerRegisterClicked = (
  posthogFromHook: TPosthogClient | undefined,
  data: TIgcseGroupClassBannerAnalyticsPayload
): boolean => {
  const ph = resolveIgcsePosthogClient(posthogFromHook);
  if (!ph) return false;
  const loaded = (ph as TPosthogWithLoaded).__loaded;
  if (!loaded) return false;
  const result = ph.capture(
    'igcse_group_class_banner_register_clicked',
    {
      userId: data.userId,
      role: data.role,
      featureFlag: data.featureFlag,
      featureFlagVariant: data.featureFlagVariant,
      countryCode: data.countryCode,
      timezone: data.timezone,
      timestamp,
    },
    POSTHOG_CAPTURE_IMMEDIATE
  );
  if (result === undefined) return false;
  flushPosthogClient(ph);
  return true;
};

export const postHogTutorNoSlotsPromptShown = (
  posthog,
  data: { userId: string; dayName: string }
) => {
  posthog.capture('tutor_no_slots_prompt_shown', {
    user_id: data.userId,
    day_name: data.dayName,
    timestamp,
  });
};

export const postHogTutorNoSlotsPromptCtaClicked = (
  posthog,
  data: { userId: string; dayName: string }
) => {
  posthog.capture('tutor_no_slots_prompt_cta_clicked', {
    user_id: data.userId,
    day_name: data.dayName,
    timestamp,
  });
};

export const postHogTutorNoSlotsPromptDismissed = (
  posthog,
  data: {
    userId: string;
    dayName: string;
    dismissType: 'close_button' | 'maybe_later' | 'backdrop';
  }
) => {
  posthog.capture('tutor_no_slots_prompt_dismissed', {
    user_id: data.userId,
    day_name: data.dayName,
    dismiss_type: data.dismissType,
    timestamp,
  });
};

export const postHogSendPaymentLinkClicked = (
  posthog,
  data: {
    userId?: string;
    name?: string;
    role?: string;
    courseId: string;
  }
) => {
  if (!posthog) return;
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('tutor_payment_request');
    posthog.capture('send_payment_link_clicked', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogPaymentLinkInvoiceSent = (
  posthog,
  data: {
    userId?: string;
    name?: string;
    role?: string;
    courseId: string;
    noOfLessons: number;
  }
) => {
  if (!posthog) return;
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('tutor_payment_request');
    posthog.capture('payment_link_invoice_sent', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogResourceButtonClickedCoursePage = (
  posthog,
  data: {
    courseId: string;
    curriculum?: string;
    subject?: string;
  }
) => {
  if (!posthog) return;
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('resource-redirection-course-page');
    posthog.capture('resource_button_clicked_course_page', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogPastPaperButtonClickedCoursePage = (
  posthog,
  data: {
    courseId: string;
    curriculum?: string;
    subject?: string;
  }
) => {
  if (!posthog) return;
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('resource-redirection-course-page');
    posthog.capture('past_paper_button_clicked_course_page', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export type TChatPartyLocalTimeAnalyticsPayload = {
  userId?: string;
  name?: string;
  role?: string;
  courseId?: string;
  conversationId?: string;
  /** IANA timezone of the other party in the conversation. */
  otherPartyTimezone: string;
  /** Part of the day it is for the other party, e.g. `Late night`. */
  otherPartyPartOfDay: string;
  /** True when it is outside 08:00 - 21:00 for the other party. */
  otherPartyOutsideWorkingHours: boolean;
};

export const postHogChatPartyLocalTimeShown = (
  posthog,
  data: TChatPartyLocalTimeAnalyticsPayload
) => {
  if (!posthog) return;
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('chat_party_local_time');
    posthog.capture('chat_party_local_time_shown', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogChatPartyLocalTimeClicked = (
  posthog,
  data: TChatPartyLocalTimeAnalyticsPayload
) => {
  if (!posthog) return;
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('chat_party_local_time');
    posthog.capture('chat_party_local_time_clicked', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export type TChatPartyResponseTimeAnalyticsPayload = {
  userId?: string;
  name?: string;
  role?: string;
  courseId?: string;
  conversationId?: string;
  /** Median reply time of the other party, in seconds. */
  otherPartyMedianResponseSeconds: number;
  /** How many observed replies the median was computed from. */
  otherPartyResponseSampleCount: number;
  /** Bucketed speed label, e.g. `Replies quickly`. */
  otherPartyResponseSpeedLabel: string;
};

export const postHogChatPartyResponseTimeShown = (
  posthog,
  data: TChatPartyResponseTimeAnalyticsPayload
) => {
  if (!posthog) return;
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('chat_party_response_time');
    posthog.capture('chat_party_response_time_shown', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogChatPartyResponseTimeClicked = (
  posthog,
  data: TChatPartyResponseTimeAnalyticsPayload
) => {
  if (!posthog) return;
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('chat_party_response_time');
    posthog.capture('chat_party_response_time_clicked', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export type TAutoLessonAnalysisAnalyticsPayload = {
  userId?: string;
  name?: string;
  role?: string;
  courseId?: string;
  courseTitle?: string;
  studentId?: string;
  /** Which of the three auto analysis switches this event came from. */
  surface: TAutoAnalysisSurface;
};

/** Fired once per surface when the auto analysis switch is rendered. */
export const postHogAutoLessonAnalysisShown = (
  posthog,
  data: TAutoLessonAnalysisAnalyticsPayload
) => {
  if (!posthog) return;
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag(AUTO_LESSON_ANALYSIS_FEATURE_FLAG);
    posthog.capture('auto_lesson_analysis_shown', {
      ...data,
      variant,
      timestamp,
    });
  });
};

/**
 * Fired every time a student flips the switch. `enabled` is the value they
 * moved it to, so opt-in and opt-out rates come off this one event.
 */
export const postHogAutoLessonAnalysisToggled = (
  posthog,
  data: TAutoLessonAnalysisAnalyticsPayload & { enabled: boolean }
) => {
  if (!posthog) return;
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag(AUTO_LESSON_ANALYSIS_FEATURE_FLAG);
    posthog.capture('auto_lesson_analysis_toggled', {
      ...data,
      variant,
      timestamp,
    });
  });
};

/**
 * The course-page switch flips optimistically and saves in the background.
 * Without this, a failed save would still be counted as an opt-in by
 * `auto_lesson_analysis_toggled` and quietly skew the numbers.
 */
export const postHogAutoLessonAnalysisSaveFailed = (
  posthog,
  data: TAutoLessonAnalysisAnalyticsPayload & { enabled: boolean }
) => {
  if (!posthog) return;
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag(AUTO_LESSON_ANALYSIS_FEATURE_FLAG);
    posthog.capture('auto_lesson_analysis_save_failed', {
      ...data,
      variant,
      timestamp,
    });
  });
};

// ---------------------------------------------------------------------------
// Course monthly plan change experiment (upgrade / downgrade)
// ---------------------------------------------------------------------------

export type TCoursePlanChangeAnalyticsPayload = {
  userId?: string;
  name?: string;
  role?: string;
  courseId?: string;
  courseTitle?: string;
  tutorId?: string;
  studentId?: string;
  studentCountry?: string;
  currency?: string;
  /** True once the student has run their plan's credits down to zero or below. */
  hasActiveMonthlyPlan?: boolean;
  /**
   * Lessons per month on the student's active plan. Undefined until the backend
   * surfaces it; without it a requested tier cannot be read as a move from one
   * plan to another, only as an absolute target.
   */
  currentLessonsPerMonth?: number;
};

/**
 * Every plan-change event carries the variant so results segment cleanly, and a
 * freshly formatted timestamp rather than the module-level `timestamp` — these
 * events are the experiment's primary data and a load-time timestamp would make
 * the whole cohort look simultaneous.
 */
const capturePlanChangeEvent = (
  posthog,
  event: string,
  data: Record<string, unknown>
) => {
  if (!posthog) return;
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag(COURSE_PLAN_CHANGE_FEATURE_FLAG);
    posthog.capture(event, {
      ...data,
      variant,
      timestamp: moment().format(timeFormat),
    });
  });
};

/**
 * A student with an active monthly plan reached the out-of-credits checkout.
 * Fires in BOTH variants — it is the experiment's denominator, so control and
 * test conversion rates are computed over the same population. Without it the
 * control group would have no comparable exposure event.
 */
export const postHogCoursePlanChangeEligible = (
  posthog,
  data: TCoursePlanChangeAnalyticsPayload
) => capturePlanChangeEvent(posthog, 'course_plan_change_eligible', data);

/** The three-route plan change screen was actually rendered (test variant only). */
export const postHogCoursePlanChangeShown = (
  posthog,
  data: TCoursePlanChangeAnalyticsPayload
) => capturePlanChangeEvent(posthog, 'course_plan_change_shown', data);

/** Student picked upgrade / top-up / downgrade. */
export const postHogCoursePlanChangeRouteSelected = (
  posthog,
  data: TCoursePlanChangeAnalyticsPayload & { route: string }
) => capturePlanChangeEvent(posthog, 'course_plan_change_route_selected', data);

/** Student picked a target tier inside the upgrade or downgrade route. */
export const postHogCoursePlanChangeTierSelected = (
  posthog,
  data: TCoursePlanChangeAnalyticsPayload & {
    route: string;
    targetLessonsPerMonth: number;
    targetMonthlyTotal?: number;
  }
) => capturePlanChangeEvent(posthog, 'course_plan_change_tier_selected', data);

/**
 * Primary conversion metric. `submitted: false` records a request whose ops
 * notification failed, so a broken webhook cannot be mistaken for low demand.
 */
export const postHogCoursePlanChangeRequested = (
  posthog,
  data: TCoursePlanChangeAnalyticsPayload & {
    route: string;
    targetLessonsPerMonth: number;
    targetPerLessonRate?: number;
    targetMonthlyTotal?: number;
    /** Distinguishes a slider value from a preset tier, so tier demand stays readable. */
    isCustom?: boolean;
    reason?: string;
    notified: boolean;
  }
) => capturePlanChangeEvent(posthog, 'course_plan_change_requested', data);

/**
 * Student chose the one-time credit top-up over changing their plan — the
 * workaround this experiment is trying to price out. Fires on the route choice,
 * before checkout; the existing `course_topup_monthly_plan_enroll_clicked`
 * still records the checkout itself.
 */
export const postHogCoursePlanChangeTopUpChosen = (
  posthog,
  data: TCoursePlanChangeAnalyticsPayload
) => capturePlanChangeEvent(posthog, 'course_plan_change_topup_chosen', data);

/** Student left the plan change screen without choosing anything. */
export const postHogCoursePlanChangeDismissed = (
  posthog,
  data: TCoursePlanChangeAnalyticsPayload & { route?: string }
) => capturePlanChangeEvent(posthog, 'course_plan_change_dismissed', data);

export const postHogLessonInsightButtonClicked = (
  posthog,
  data: {
    sectionId: string;
    courseId?: string;
    sectionType?: string;
    userRole?: string;
  }
) => {
  if (!posthog) return;
  posthog.capture('lesson_insight_button_clicked', {
    ...data,
    timestamp,
  });
};

export type TSubstituteTutorBookingAnalyticsPayload = {
  userId?: string;
  name?: string;
  role?: string;
  courseId?: string;
  primaryTutorId?: string;
  /** How many real slots the primary tutor is offering on the selected date. */
  primaryTutorSlotCount: number;
  /** Date the student was looking at, `YYYY-MM-DD`. */
  selectedDate?: string;
  /** Lesson length in minutes the student had chosen. */
  selectedDuration: number;
};

const captureSubstituteTutorBookingEvent = (
  posthog,
  event: string,
  data: TSubstituteTutorBookingAnalyticsPayload & Record<string, unknown>
) => {
  if (!posthog) return;
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag(
      SUBSTITUTE_TUTOR_BOOKING_FEATURE_FLAG
    );
    posthog.capture(event, {
      ...data,
      variant,
      timestamp,
    });
  });
};

/**
 * The substitute-tutor entry point was rendered, i.e. the student hit a date
 * where the primary tutor is short on slots. This is the denominator for the
 * experiment: how often the drop-off moment actually occurs.
 */
export const postHogSubstituteTutorOptionShown = (
  posthog,
  data: TSubstituteTutorBookingAnalyticsPayload
) =>
  captureSubstituteTutorBookingEvent(
    posthog,
    'substitute_tutor_option_shown',
    data
  );

/** Student opened the substitute slot list - the first real signal of intent. */
export const postHogSubstituteTutorOptionExpanded = (
  posthog,
  data: TSubstituteTutorBookingAnalyticsPayload & { previewSlotCount: number }
) =>
  captureSubstituteTutorBookingEvent(
    posthog,
    'substitute_tutor_option_expanded',
    data
  );

/** Student picked one of the slots, i.e. a specific time they want covered. */
export const postHogSubstituteTutorSlotSelected = (
  posthog,
  data: TSubstituteTutorBookingAnalyticsPayload & {
    selectedSlotStart: string;
  }
) =>
  captureSubstituteTutorBookingEvent(
    posthog,
    'substitute_tutor_slot_selected',
    data
  );

/**
 * Conversion event for the experiment: the student pressed Book on a
 * substitute tutor slot. The surface reads as a live booking flow up to this
 * point, so this is an intent-to-book rather than a "notify me" click — it is
 * the number that should decide whether the matching service gets built. The
 * student sees the "rolling out shortly" message immediately after this fires.
 *
 * A slot is always selected before the button is enabled, so `hasSelectedSlot`
 * is true in practice; it is kept so the payload stays readable if the flow
 * ever allows booking without a time.
 */
export const postHogSubstituteTutorInterestRegistered = (
  posthog,
  data: TSubstituteTutorBookingAnalyticsPayload & {
    selectedSlotStart?: string;
    hasSelectedSlot: boolean;
  }
) =>
  captureSubstituteTutorBookingEvent(
    posthog,
    'substitute_tutor_interest_registered',
    data
  );

/**
 * Recurring-class upsell shown in the Book a Lesson modal once a student
 * picks a slot and presses Book (behind the recurring-booking-new flag, test
 * variant). Tracking only — nothing is actually booked recurringly, this
 * measures how many students would want it before it's built.
 */
type TRecurringClassPromptAnalyticsPayload = {
  userId: string;
  courseId: string;
  tutorId: string;
  dayName: string;
};

export const postHogRecurringClassPromptShown = (
  posthog,
  data: TRecurringClassPromptAnalyticsPayload
) => {
  posthog.capture('recurring_booking_popup_shown', {
    user_id: data.userId,
    course_id: data.courseId,
    tutor_id: data.tutorId,
    day_name: data.dayName,
    timestamp,
  });
};

export const postHogRecurringClassPromptBookRecurringClicked = (
  posthog,
  data: TRecurringClassPromptAnalyticsPayload
) => {
  posthog.capture('recurring_book_button_clicked', {
    user_id: data.userId,
    course_id: data.courseId,
    tutor_id: data.tutorId,
    day_name: data.dayName,
    timestamp,
  });
};

/** Feature flag gating the subscription explainer link in the enrol modal. */
export const SUBSCRIPTION_EXPLAINER_FLAG = 'subscription_explainer_link';

export type TSubscriptionExplainerAnalyticsPayload = {
  courseId?: string;
  /** Billing cycle of the plan selected when the link was seen or clicked. */
  billingInterval?: 'month' | 'quarter';
  /** Lessons per month of the selected plan, 0 when nothing is selected yet. */
  lessonsPerMonth?: number;
  /** True when the selected plan came from the custom lessons slider. */
  isCustomLessonsSelected?: boolean;
};

export const postHogSubscriptionExplainerShown = (
  posthog,
  data: TSubscriptionExplainerAnalyticsPayload
) => {
  if (!posthog) return;
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag(SUBSCRIPTION_EXPLAINER_FLAG);
    posthog.capture('subscription_explainer_shown', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogSubscriptionExplainerClicked = (
  posthog,
  data: TSubscriptionExplainerAnalyticsPayload
) => {
  if (!posthog) return;
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag(SUBSCRIPTION_EXPLAINER_FLAG);
    posthog.capture('subscription_explainer_clicked', {
      ...data,
      variant,
      timestamp,
    });
  });
};

// ---------------------------------------------------------------------------
// Course monthly plan upgrade (course_plan_upgrade: custom top-up → real plan change)
// ---------------------------------------------------------------------------

export type TCoursePlanUpgradeAnalyticsPayload = {
  userId?: string;
  name?: string;
  role?: string;
  courseId?: string;
  courseTitle?: string;
  tutorId?: string;
  studentId?: string;
  studentCountry?: string;
  currency?: string;
  /** Lessons per month on the plan being replaced. */
  currentLessonsPerMonth?: number;
  /** Billing interval of the plan being replaced ('quarter' plans become monthly). */
  currentBillingInterval?: 'month' | 'quarter';
};

/**
 * Stamps every upgrade event with the course_plan_upgrade variant so control
 * (custom top-up) and test (plan change) segment cleanly, with a fresh
 * timestamp rather than the module-level one.
 */
const captureCoursePlanUpgradeEvent = (
  posthog,
  event: string,
  data: Record<string, unknown>
) => {
  if (!posthog) return;
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag(COURSE_PLAN_UPGRADE_FEATURE_FLAG);
    posthog.capture(event, {
      ...data,
      variant,
      timestamp: moment().format(timeFormat),
    });
  });
};

/** The "Upgrade Your Plan" screen was rendered (test variant only). */
export const postHogCoursePlanUpgradeShown = (
  posthog,
  data: TCoursePlanUpgradeAnalyticsPayload
) => captureCoursePlanUpgradeEvent(posthog, 'course_plan_upgrade_shown', data);

/** The student pressed Enrol on the upgrade screen. */
export const postHogCoursePlanUpgradeSubmitted = (
  posthog,
  data: TCoursePlanUpgradeAnalyticsPayload & {
    direction: 'upgrade' | 'downgrade' | 'same';
    targetLessonsPerMonth: number;
    perLessonRate: number;
    totalAmount: number;
    resourceFeeChecked: boolean;
    autoAnalysisChecked: boolean;
  }
) =>
  captureCoursePlanUpgradeEvent(posthog, 'course_plan_upgrade_submitted', data);

/** The backend confirmed the plan change and charged the new cycle. */
export const postHogCoursePlanUpgradeSuccess = (
  posthog,
  data: TCoursePlanUpgradeAnalyticsPayload & {
    direction: 'upgrade' | 'downgrade' | 'same';
    targetLessonsPerMonth: number;
    amount: number;
    creditsAdded: number;
    nextRenewalAt?: string | null;
  }
) =>
  captureCoursePlanUpgradeEvent(posthog, 'course_plan_upgrade_success', data);

/** The plan change was refused (declined card, inactive plan, …). */
export const postHogCoursePlanUpgradeFailed = (
  posthog,
  data: TCoursePlanUpgradeAnalyticsPayload & {
    direction: 'upgrade' | 'downgrade' | 'same';
    targetLessonsPerMonth: number;
    errorMessage: string;
  }
) => captureCoursePlanUpgradeEvent(posthog, 'course_plan_upgrade_failed', data);
